import React, { useState } from 'react';
import toast from 'react-hot-toast';
import type { InterviewSlot, InterviewStatus } from '../../types/aiRecruiter';

import CloseXIcon from '../../assets/icons/close-x.svg';
import BriefcaseIcon from '../../assets/icons/briefcase.svg';
import CalendarOrangeIcon from '../../assets/icons/calendar-orange.svg';
import ClockOrangeIcon from '../../assets/icons/clock-orange.svg';
import VideoOrangeIcon from '../../assets/icons/video-orange.svg';
import PhoneContactIcon from '../../assets/icons/phone-contact.svg';
import MailContactIcon from '../../assets/icons/mail-contact.svg';
import ChatContactIcon from '../../assets/icons/chat-contact.svg';
import LinkExternalIcon from '../../assets/icons/link-external.svg';
import ExternalArrowIcon from '../../assets/icons/external-arrow.svg';

interface InterviewDetailsModalProps {
	interview: InterviewSlot;
	onClose: () => void;
	onConfirm: () => void;
	onReject: () => void;
	onComplete?: () => void;
	onApprove?: () => void;
	onRejectCandidate?: () => void;
	onCancelInterview?: () => void;
}

const formatModalDate = (dateStr: string): string => {
	const date = new Date(dateStr);
	const weekday = date.toLocaleDateString('ru-RU', { weekday: 'long' });
	const dayMonth = date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
	return `${weekday}, ${dayMonth}`;
};

const formatModalTime = (timeStr: string): string => {
	const parts = timeStr.split(':');
	const hours = Number(parts[0]);
	const minutes = Number(parts[1]);
	if (Number.isNaN(hours) || Number.isNaN(minutes)) return timeStr;
	const endHours = (hours + 1) % 24;
	return `${timeStr} - ${String(endHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const getModalStatusBadge = (status: InterviewStatus) => {
	if (status === 'BOOKED') {
		return (
			<div className="inline-flex items-center gap-1 px-[10px] py-[2px] rounded-full bg-[#FEF3C6]">
				<span className="text-[12px] font-medium leading-[16px] text-[#973C00]">Требует подтверждения</span>
			</div>
		);
	}
	if (status === 'CONFIRMED') {
		return (
			<div className="inline-flex items-center gap-1 px-[10px] py-[2px] rounded-full bg-[#DBEAFE]">
				<span className="text-[12px] font-medium leading-[16px] text-[#1E40AF]">Подтверждено</span>
			</div>
		);
	}
	if (status === 'COMPLETED') {
		return (
			<div className="inline-flex items-center gap-1 px-[10px] py-[2px] rounded-full bg-[#D1FAE5]">
				<span className="text-[12px] font-medium leading-[16px] text-[#065F46]">Завершено</span>
			</div>
		);
	}
	if (status === 'CANCELLED') {
		return (
			<div className="inline-flex items-center gap-1 px-[10px] py-[2px] rounded-full bg-[#FEE2E2]">
				<span className="text-[12px] font-medium leading-[16px] text-[#991B1B]">Отменено</span>
			</div>
		);
	}
	if (status === 'APPROVED') {
		return (
			<div className="inline-flex items-center gap-1 px-[10px] py-[2px] rounded-full bg-[#ECFDF5]">
				<span className="text-[12px] font-medium leading-[16px] text-[#007A55]">Одобрен</span>
			</div>
		);
	}
	if (status === 'REJECTED') {
		return (
			<div className="inline-flex items-center gap-1 px-[10px] py-[2px] rounded-full bg-[#FDEDF0]">
				<span className="text-[12px] font-medium leading-[16px] text-[#AF1D38]">Отклонен</span>
			</div>
		);
	}
	if (status === 'NO_SHOW') {
		return (
			<div className="inline-flex items-center gap-1 px-[10px] py-[2px] rounded-full bg-[#FFEDD5]">
				<span className="text-[12px] font-medium leading-[16px] text-[#9A3412]">Не явился</span>
			</div>
		);
	}
	return null;
};

const showInDevelopmentToast = () => {
	toast.error('Находится в разработке');
};

const InterviewDetailsModal: React.FC<InterviewDetailsModalProps> = ({
	interview,
	onClose,
	onConfirm,
	onReject,
	onComplete,
	onApprove,
	onRejectCandidate,
	onCancelInterview
}) => {
	const [isEditingMeetLink, setIsEditingMeetLink] = useState(false);
	const [meetLinkValue, setMeetLinkValue] = useState(interview.meetLink || '');

	const handleMeetLinkSave = () => {
		setIsEditingMeetLink(false);
	};

	const handleMeetLinkKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleMeetLinkSave();
		} else if (e.key === 'Escape') {
			setMeetLinkValue(interview.meetLink || '');
			setIsEditingMeetLink(false);
		}
	};

	return (
		<div
			className="fixed inset-0 z-50 flex justify-end"
			onClick={onClose}
		>
			<div className="absolute inset-0 bg-black/30" />

			<div
				className="relative w-[480px] h-full bg-[#F5F3F0] flex flex-col justify-between"
				onClick={e => e.stopPropagation()}
			>
				<div className="bg-white px-5 pt-6 pb-4 border-b border-[#F1F5F9]">
					<div className="flex items-center justify-between mb-4">
						{getModalStatusBadge(interview.status)}
						<button
							type="button"
							onClick={onClose}
							className="w-6 h-6 flex items-center justify-center text-[#62748E] hover:text-[#0F172B] transition-colors"
						>
							<img src={CloseXIcon} alt="Закрыть" className="w-6 h-6" />
						</button>
					</div>

					<div className="flex items-center gap-3">
						<div className="w-14 h-14 rounded-full bg-[#E2E4E9] flex items-center justify-center text-[#62748E] text-[18px] font-normal shrink-0">
							{interview.candidateName
								? interview.candidateName
										.split(' ')
										.filter(Boolean)
										.map(n => n[0])
										.join('')
										.slice(0, 2)
										.toUpperCase()
								: '??'}
						</div>
						<div className="flex flex-col gap-1">
							<h2 className="text-[18px] leading-[22px] font-bold text-[#0F172B] tracking-[-0.02em]">
								{interview.candidateName || 'Не указано'}
							</h2>
							<div className="flex items-center gap-[6px] text-[#62748E]">
								<img src={BriefcaseIcon} alt="" className="w-[14px] h-[14px]" />
								<span className="text-[14px] leading-[20px] font-medium">
									{interview.campaignName || 'Прямая запись'}
								</span>
							</div>
						</div>
					</div>
				</div>

				<div className="flex-1 overflow-y-auto px-5 py-5">
					<div className="bg-white rounded-[14px] mb-6">
						<div className="flex items-center gap-3 px-4 py-4 border-b border-[#F8FAFC]">
							<img src={CalendarOrangeIcon} alt="" className="w-10 h-10" />
							<div className="flex flex-col gap-[2px]">
								<span className="text-[12px] leading-[16px] text-[#62748E]">Дата</span>
								<span className="text-[16px] leading-[24px] font-medium text-[#0F172B] tracking-[-0.02em] capitalize">
									{formatModalDate(interview.slotDate)}
								</span>
							</div>
						</div>

						<div className="flex items-center gap-3 px-4 py-4 border-b border-[#F8FAFC]">
							<img src={ClockOrangeIcon} alt="" className="w-10 h-10" />
							<div className="flex flex-col gap-[2px]">
								<span className="text-[12px] leading-[16px] text-[#62748E]">Время</span>
								<span className="text-[16px] leading-[24px] font-medium text-[#0F172B] tracking-[-0.02em]">
									{formatModalTime(interview.slotTime)}
								</span>
							</div>
						</div>

						<div className="flex items-center gap-3 px-4 py-4">
							<img src={VideoOrangeIcon} alt="" className="w-10 h-10" />
							<div className="flex flex-col flex-1 min-w-0">
								<span className="text-[14px] leading-[20px] font-medium text-[#0F172B]">Google Meet</span>
								{isEditingMeetLink ? (
									<input
										type="text"
										value={meetLinkValue}
										onChange={(e) => setMeetLinkValue(e.target.value)}
										onBlur={handleMeetLinkSave}
										onKeyDown={handleMeetLinkKeyDown}
										placeholder="Вставьте ссылку"
										autoFocus
										className="text-[12px] leading-[16px] text-[#253EA7] bg-transparent border-none outline-none p-0 m-0 w-full"
									/>
								) : meetLinkValue ? (
									<div className="flex items-center gap-2 min-w-0">
										<a
											href={meetLinkValue}
											target="_blank"
											rel="noopener noreferrer"
											className="text-[12px] leading-[16px] text-[#253EA7] hover:underline truncate"
										>
											{meetLinkValue}
										</a>
										<button
											type="button"
											onClick={() => setIsEditingMeetLink(true)}
											className="text-[10px] text-[#62748E] hover:text-[#253EA7] transition-colors shrink-0"
										>
											✎
										</button>
									</div>
								) : (
									<button
										type="button"
										onClick={() => setIsEditingMeetLink(true)}
										className="text-[12px] leading-[16px] text-[#62748E] hover:text-[#253EA7] text-left transition-colors"
									>
										Введите ссылку
									</button>
								)}
							</div>
						</div>
					</div>

					<div className="mb-6">
						<h3 className="text-[12px] leading-[16px] font-semibold text-[#62748E] uppercase tracking-[0.05em] px-1 mb-2">
							Контакты
						</h3>
						<div className="flex gap-2">
							<a
								href={`tel:${interview.candidatePhone}`}
								className="flex-1 flex flex-col items-center gap-2 py-4 bg-white border border-[#F1F5F9] rounded-[14px] hover:bg-[#F8FAFC] transition-colors"
							>
								<img src={PhoneContactIcon} alt="" className="w-5 h-5" />
								<span className="text-[12px] leading-[16px] font-medium text-[#0F172B]">
									{interview.candidatePhone || '—'}
								</span>
							</a>
							<button
								type="button"
								onClick={showInDevelopmentToast}
								className="flex-1 flex flex-col items-center gap-2 py-4 bg-white border border-[#F1F5F9] rounded-[14px] hover:bg-[#F8FAFC] transition-colors"
							>
								<img src={MailContactIcon} alt="" className="w-5 h-5" />
								<span className="text-[12px] leading-[16px] font-medium text-[#0F172B]">Email</span>
							</button>
							<button
								type="button"
								onClick={showInDevelopmentToast}
								className="flex-1 flex flex-col items-center gap-2 py-4 bg-white border border-[#F1F5F9] rounded-[14px] hover:bg-[#F8FAFC] transition-colors"
							>
								<img src={ChatContactIcon} alt="" className="w-5 h-5" />
								<span className="text-[12px] leading-[16px] font-medium text-[#0F172B]">Чат</span>
							</button>
						</div>
					</div>

					<div>
						<h3 className="text-[12px] leading-[16px] font-semibold text-[#62748E] uppercase tracking-[0.05em] px-1 mb-2">
							Материалы
						</h3>
						<div className="flex flex-col gap-2">
							<button
								type="button"
								onClick={showInDevelopmentToast}
								className="flex items-center gap-3 px-4 py-3 bg-white border border-[#F1F5F9] rounded-[14px] hover:bg-[#F8FAFC] transition-colors text-left"
							>
								<img src={LinkExternalIcon} alt="" className="w-5 h-5" />
								<div className="flex-1 flex flex-col">
									<span className="text-[14px] leading-[20px] font-medium text-[#0F172B]">HeadHunter</span>
									<span className="text-[12px] leading-[16px] text-[#62748E]">Профиль</span>
								</div>
								<img src={ExternalArrowIcon} alt="" className="w-4 h-4" />
							</button>
						</div>
					</div>
				</div>

				{interview.status === 'BOOKED' && (
					<div className="bg-white px-5 py-5 flex gap-3 border-t border-[#F1F5F9]">
						<button
							type="button"
							onClick={onConfirm}
							className="flex-1 h-12 flex items-center justify-center rounded-[14px] bg-[#009966] text-white text-[14px] font-medium hover:bg-[#008855] transition-colors"
						>
							Подтвердить слот
						</button>
						<button
							type="button"
							onClick={onReject}
							className="flex-1 h-12 flex items-center justify-center rounded-[14px] border border-[#DF1C41] text-[#DF1C41] text-[14px] font-medium hover:bg-[#FEF2F2] transition-colors"
						>
							Отклонить слот
						</button>
					</div>
				)}
				{interview.status === 'CONFIRMED' && (
					<div className="bg-white px-5 py-5 flex gap-3 border-t border-[#F1F5F9]">
						<button
							type="button"
							onClick={onApprove}
							className="flex-1 h-12 flex items-center justify-center rounded-[14px] bg-[#009966] text-white text-[14px] font-medium hover:bg-[#008855] transition-colors"
						>
							Одобрить
						</button>
						<button
							type="button"
							onClick={onRejectCandidate}
							className="flex-1 h-12 flex items-center justify-center rounded-[14px] border border-[#AF1D38] text-[#AF1D38] text-[14px] font-medium hover:bg-[#FDEDF0] transition-colors"
						>
							Отклонить
						</button>
						<button
							type="button"
							onClick={onCancelInterview}
							className="flex-1 h-12 flex items-center justify-center rounded-[14px] border border-[#1447E6] text-[#1447E6] text-[14px] font-medium hover:bg-[#EFF6FF] transition-colors"
						>
							Отменить
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

export default InterviewDetailsModal;
